/* LAV TDMS — пакеты работ: прогресс со свёрткой к родителю и вехи. */
import { useMemo, useState } from 'react'
import { Badge, Button, Dialog, EmptyState, Field, Input, PageHead, Select, Spinner, toast, railWidth } from '@/ui'
import { Icon } from '@/ds/icons'
import { qk, useSub } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import { useMe } from '@/data/meta'
import { exportRows } from '@/lib/excel'
import { t, type Lang } from '@/i18n/lang'

/** Узел разбивки. Дерево приходит плоским списком — его строит экран. */
interface WorkPackage {
  code: string
  parent: string | null
  title: string
  weight: number
  note: string | null
  /** Свои документы узла и всего в поддереве. */
  docs: number
  docsTotal: number
  progress: number
  empty: boolean
}

interface Milestone {
  code: string
  package: string
  packageTitle: string | null
  title: string
  life: string
  plan: string | null
  fact: string | null
  docs: number
  reached: number
  state: 'plan' | 'done' | 'overdue'
  overdueDays: number
  note: string | null
}

interface Tree {
  packages: WorkPackage[]
  milestones: Milestone[]
  weights: Record<string, number>
  /** Сколько документов проекта ещё не разложено: пустая строка тоже новость. */
  unassigned: number
}

interface PackageDocs {
  code: string
  title: string
  weight: number
  parent: string | null
  docs: {
    no: string
    title: string | null
    disc: string | null
    rev: string | null
    life: string
    area: string | null
    plan: string | null
    fact: string | null
    /** Вес позиции: во столько раз документ тяжелее прочих в этом пакете. */
    weight: number
    progress: number | null
  }[]
}

/** Точка S-кривой: сколько процентов должно быть и сколько есть. */
interface CurvePoint {
  label: string
  plan: number
  fact: number | null
  past: boolean
}

interface Curve {
  points: CurvePoint[]
  /** Последний прошедший месяц: правее него факта ещё нет. */
  currentIndex: number
}

const LIFE: Record<string, { ru: string; en: string }> = {
  planned: { ru: 'запланирован', en: 'planned' },
  received: { ru: 'получен', en: 'received' },
  review: { ru: 'на рассмотрении', en: 'in review' },
  crs: { ru: 'замечания', en: 'comments' },
  reissue: { ru: 'переиздание', en: 'reissue' },
  approved: { ru: 'принят с замечаниями', en: 'accepted with comments' },
  accepted: { ru: 'принят — ожидается AFD', en: 'accepted — AFD pending' },
  afd: { ru: 'согласован', en: 'approved' },
  void: { ru: 'аннулирован', en: 'void' },
}

const lifeName = (life: string, lang: Lang) => t(lang, LIFE[life]?.ru ?? life, LIFE[life]?.en ?? life)

/**
 * Прогресс по пакетам работ.
 *
 * <p>Разрез по MDR отвечает на вопрос «что прислал подрядчик», а этот — «что
 * сделано по составу работ». Цифра считается сервером: доля стадии у документа,
 * среднее по пакету, взвешенная сумма у родителя. На клиенте ничего не
 * пересчитывается — вторая версия арифметики разошлась бы с первой.
 */
export function Progress({ lang = 'ru' }: { lang?: Lang }) {
  const me = useMe().data
  const tree = useSub<Tree>('/work-packages')
  const data = tree.data
  const [chosen, setChosen] = useState<string>('')
  const [creating, setCreating] = useState<null | { parent: string | null }>(null)
  const [editing, setEditing] = useState<WorkPackage | null>(null)
  const [assigning, setAssigning] = useState<string | null>(null)
  const [milestoning, setMilestoning] = useState<string | null>(null)
  const [weights, setWeights] = useState(false)

  /* Разбивка работ — настройка проекта, а не администрирование людей (V64). */
  const mayEdit = me?.perms?.dicts === 'rw'
  const packages = useMemo(() => order(data?.packages ?? []), [data?.packages])
  const code = chosen || packages[0]?.code || ''
  const contents = useSub<PackageDocs>(code ? '/work-packages/' + encodeURIComponent(code) : null)
  /* Кривая отдельным запросом и по выбранному узлу: считается она по всему его
     поддереву и от правки одного веса не меняется настолько, чтобы платить за
     неё при каждом сохранении разбивки. */
  const curve = useSub<Curve>(code ? '/work-packages/curve?wp=' + encodeURIComponent(code) : null)

  const del = useCommand<{ code: string }>({
    method: 'DELETE',
    path: (a) => '/work-packages/' + encodeURIComponent(a.code),
    invalidate: () => [qk.sub('/work-packages'), qk.all('audit')],
  })

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{me?.project ?? ''} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Прогресс', 'Progress')}</span>}
        title={t(lang, 'Пакеты работ и прогресс', 'Work packages and progress')}
        count={packages.length}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" disabled={!packages.length} iconLeft={<Icon name="download" size={14} />}
                    onClick={() => exportRows<WorkPackage>(packages, [
                      ['Код', (r) => r.code],
                      ['Наименование', (r) => r.title],
                      ['Родитель', (r) => r.parent ?? ''],
                      ['Вес', (r) => r.weight],
                      ['Документов', (r) => r.docsTotal],
                      ['Прогресс, %', (r) => r.progress],
                    ], 'work-packages.xlsx', t(lang, 'Прогресс', 'Progress'), lang)}>
              {t(lang, 'Выгрузить (xlsx)', 'Export (xlsx)')}
            </Button>
            {mayEdit ? (
              <>
                <Button variant="secondary" size="sm" iconLeft={<Icon name="sliders-horizontal" size={14} />}
                        onClick={() => setWeights(true)}>{t(lang, 'Веса стадий', 'Stage weights')}</Button>
                <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />}
                        onClick={() => setCreating({ parent: null })}>{t(lang, 'Новый пакет', 'New package')}</Button>
              </>
            ) : null}
          </div>
        } />

      {data && data.unassigned > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-sunken)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          <Icon name="alert-triangle" size={14} />
          {t(lang, `Не разложено по пакетам: ${data.unassigned} документов. В прогресс они не входят.`,
                `${data.unassigned} documents are not in any package. They are outside the progress figure.`)}
        </div>
      ) : null}

      {curve.data && curve.data.points.length ? <PlanFactCurve lang={lang} curve={curve.data} node={code} /> : null}

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <div style={{ width: railWidth(380), flex: '0 0 auto', borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto' }}>
          {tree.isPending ? <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div> : null}
          {packages.map((p) => (
            <button key={p.code} onClick={() => setChosen(p.code)} className="tdms-rail"
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 12px', paddingLeft: 12 + depth(p, data?.packages ?? []) * 16, border: 0, borderBottom: '1px solid var(--border-subtle)', background: p.code === code ? 'var(--accent-soft)' : 'transparent', boxShadow: p.code === code ? 'inset 2px 0 0 var(--accent)' : 'none', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{p.code}</span>
                {p.weight !== 1 ? <Badge tone="info" size="sm">×{p.weight}</Badge> : null}
                <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{p.progress}%</span>
              </div>
              <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', marginTop: 2 }}>{p.title}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                <span style={{ flex: 1, height: 4, borderRadius: 2, background: 'var(--surface-sunken)', overflow: 'hidden' }}>
                  {/* Готовность растёт, а не подменяется: после отметки и рефетча полоса доезжает до нового значения. */}
                  <span style={{ display: 'block', height: '100%', width: p.progress + '%', background: p.progress >= 100 ? 'var(--green-500)' : 'var(--accent)', transition: 'width var(--duration-slow) var(--ease-standard)' }} />
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>
                  {p.docsTotal} {t(lang, 'док.', 'docs')}
                </span>
              </div>
            </button>
          ))}
          {!tree.isPending && !packages.length ? (
            <div style={{ padding: 16, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {t(lang, 'Пакетов работ пока нет. Заведите верхний узел разбивки, вложите в него подпакеты и разложите документы.',
                    'No work packages yet. Create a top node, nest children and assign documents.')}
            </div>
          ) : null}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {!data || !code ? (
            <EmptyState icon="layers" title={t(lang, 'Пакет не выбран', 'No package selected')}
                        text={t(lang, 'Выберите пакет слева.', 'Pick a package on the left.')} />
          ) : (
            <PackageBody
              lang={lang}
              mayEdit={mayEdit}
              node={packages.find((p) => p.code === code) ?? null}
              docs={contents.data}
              milestones={(data.milestones ?? []).filter((m) => m.package === code)}
              onEdit={() => setEditing(packages.find((p) => p.code === code) ?? null)}
              onChild={() => setCreating({ parent: code })}
              onAssign={() => setAssigning(code)}
              onMilestone={() => setMilestoning(code)}
              onDelete={() => del.mutate({ code }, {
                onSuccess: () => { setChosen(''); toast({ tone: 'success', title: t(lang, 'Пакет снят', 'Package removed') }) },
              })}
            />
          )}
        </div>
      </div>

      {creating ? <PackageDialog lang={lang} parent={creating.parent} packages={packages}
                                 onClose={() => setCreating(null)} onDone={setChosen} /> : null}
      {editing ? <PackageDialog lang={lang} edit={editing} packages={packages}
                                onClose={() => setEditing(null)} onDone={setChosen} /> : null}
      {assigning ? <AssignDialog lang={lang} code={assigning} onClose={() => setAssigning(null)} /> : null}
      {milestoning ? <MilestoneDialog lang={lang} code={milestoning} onClose={() => setMilestoning(null)} /> : null}
      {weights && data ? <WeightsDialog lang={lang} weights={data.weights} onClose={() => setWeights(false)} /> : null}
    </div>
  )
}

/**
 * S-кривая «план / факт».
 *
 * <p>Отвечает на строку ежемесячного отчёта: «должно быть 62 %, фактически
 * 54 %». Обе кривые считает сервер по плановым и фактическим датам стадий —
 * на клиенте ничего не пересчитывается, вторая версия арифметики разошлась бы
 * с деревом.
 */
function PlanFactCurve({ lang, curve, node }: { lang: Lang; curve: Curve; node: string }) {
  const pts = curve.points
  const here = curve.currentIndex >= 0 ? pts[curve.currentIndex] : undefined
  const gap = here && here.fact != null ? Math.round((here.fact - here.plan) * 10) / 10 : null
  const w = 760
  const h = 150
  const padL = 26
  const padB = 16
  const X = (i: number) => padL + (pts.length > 1 ? (i / (pts.length - 1)) * (w - padL - 8) : (w - padL - 8) / 2)
  const Y = (v: number) => 8 + (1 - v / 100) * (h - 8 - padB)
  const line = (get: (p: CurvePoint) => number | null) => pts
    .map((p, i) => { const v = get(p); return v == null ? null : (i && get(pts[i - 1]!) != null ? 'L' : 'M') + X(i) + ' ' + Y(v) })
    .filter(Boolean).join(' ')

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 18, padding: '10px 16px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-card)' }}>
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} style={{ display: 'block' }}>
          {[0, 50, 100].map((g) => (
            <g key={g}>
              <line x1={padL} y1={Y(g)} x2={w - 8} y2={Y(g)} stroke="var(--border-subtle)" strokeWidth="1" />
              <text x={padL - 5} y={Y(g) + 3} textAnchor="end" fontSize="9" fill="var(--text-subtle)" fontFamily="var(--font-mono)">{g}</text>
            </g>
          ))}
          {pts.map((p, i) => (i % (pts.length > 10 ? 3 : 1) === 0
            ? <text key={p.label} x={X(i)} y={h - 3} textAnchor="middle" fontSize="9" fill="var(--text-subtle)" fontFamily="var(--font-mono)">{p.label}</text>
            : null))}
          <path d={line((p) => p.plan)} fill="none" stroke="var(--slate-400)" strokeWidth="1.6" strokeDasharray="5 4" />
          <path d={line((p) => p.fact)} fill="none" stroke="var(--green-500)" strokeWidth="2.2" />
        </svg>
      </div>
      <div style={{ width: 150, flex: '0 0 auto', display: 'grid', gap: 6, borderLeft: '1px solid var(--border-subtle)', paddingLeft: 16 }}>
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', fontFamily: 'var(--font-mono)' }}>
          {t(lang, 'План / факт', 'Plan / actual')} · {node}
        </div>
        <Metric label={t(lang, 'План на сегодня', 'Plan to date')} value={here ? here.plan + '%' : '—'} color="--text-muted" />
        <Metric label={t(lang, 'Факт на сегодня', 'Actual to date')} value={here && here.fact != null ? here.fact + '%' : '—'} color="--green-600" />
        <Metric label={t(lang, 'Отклонение', 'Variance')} value={gap == null ? '—' : gap + ' ' + t(lang, 'п.п.', 'pp')}
                color={gap == null ? '--text-subtle' : gap < 0 ? '--danger-ink' : '--green-600'} />
      </div>
    </div>
  )
}

function Metric({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div>
      <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 600, color: `var(${color})` }}>{value}</div>
    </div>
  )
}

/** Плоский список в порядке обхода дерева: родитель, затем его дети. */
function order(rows: WorkPackage[]): WorkPackage[] {
  const byParent = new Map<string, WorkPackage[]>()
  const known = new Set(rows.map((r) => r.code))
  for (const r of rows) {
    const key = r.parent && known.has(r.parent) ? r.parent : ''
    const list = byParent.get(key) ?? []
    list.push(r)
    byParent.set(key, list)
  }
  const out: WorkPackage[] = []
  const walk = (parent: string, guard: Set<string>) => {
    for (const r of byParent.get(parent) ?? []) {
      if (guard.has(r.code)) continue
      guard.add(r.code)
      out.push(r)
      walk(r.code, guard)
    }
  }
  walk('', new Set())
  return out
}

/** Глубина узла — только для отступа в списке. */
function depth(node: WorkPackage, rows: WorkPackage[]): number {
  const byCode = new Map(rows.map((r) => [r.code, r]))
  let d = 0
  let cur = node.parent
  const seen = new Set<string>()
  while (cur && byCode.has(cur) && !seen.has(cur)) {
    seen.add(cur)
    d += 1
    cur = byCode.get(cur)?.parent ?? null
  }
  return d
}

function PackageBody({ lang, mayEdit, node, docs, milestones, onEdit, onChild, onAssign, onMilestone, onDelete }: {
  lang: Lang
  mayEdit: boolean
  node: WorkPackage | null
  docs: PackageDocs | undefined
  milestones: Milestone[]
  onEdit: () => void
  onChild: () => void
  onAssign: () => void
  onMilestone: () => void
  onDelete: () => void
}) {
  const unassign = useCommand<{ code: string; doc: string }>({
    method: 'DELETE',
    path: (a) => '/work-packages/' + encodeURIComponent(a.code) + '/docs/' + encodeURIComponent(a.doc),
    invalidate: () => [qk.sub('/work-packages'), qk.all('audit')],
  })
  const dropMilestone = useCommand<{ code: string }>({
    method: 'DELETE',
    path: (a) => '/milestones/' + encodeURIComponent(a.code),
    invalidate: () => [qk.sub('/work-packages'), qk.all('audit')],
  })
  if (!node) return null
  const rows = docs?.docs ?? []

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{node.title}</span>
            <Badge tone={node.progress >= 100 ? 'success' : 'neutral'}>{node.progress}%</Badge>
          </div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>
            {node.code}{node.parent ? ' · ' + t(lang, 'в', 'in') + ' ' + node.parent : ''} · {t(lang, 'вес', 'weight')} {node.weight} · {node.docs} {t(lang, 'своих', 'own')} / {node.docsTotal} {t(lang, 'всего', 'total')}
          </div>
        </div>
        {mayEdit ? (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={onChild}>{t(lang, 'Подпакет', 'Child')}</Button>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="file-plus" size={14} />} onClick={onAssign}>{t(lang, 'Разложить документы', 'Assign documents')}</Button>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="flag" size={14} />} onClick={onMilestone}>{t(lang, 'Веха', 'Milestone')}</Button>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={14} />} onClick={onEdit}>{t(lang, 'Изменить', 'Edit')}</Button>
            <Button variant="secondary" size="sm" iconLeft={<Icon name="trash-2" size={14} />} onClick={onDelete}>{t(lang, 'Снять', 'Remove')}</Button>
          </div>
        ) : null}
      </div>

      {milestones.length ? (
        <div style={{ borderBottom: '1px solid var(--border-subtle)' }}>
          {milestones.map((m) => (
            <div key={m.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
              <Icon name="flag" size={14} />
              <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text-strong)' }}>{m.title}</span>
              <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
                {t(lang, 'до стадии', 'through')} «{lifeName(m.life, lang)}» · {m.reached}/{m.docs}
              </span>
              <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{m.plan}</span>
                {m.state === 'done' ? <Badge tone="success" size="sm">{t(lang, 'достигнута', 'reached')} {m.fact ?? ''}</Badge>
                  : m.state === 'overdue' ? <Badge tone="danger" size="sm">{t(lang, `просрочена на ${m.overdueDays} дн.`, `${m.overdueDays} days late`)}</Badge>
                  : <Badge tone="neutral" size="sm">{t(lang, 'в плане', 'planned')}</Badge>}
                {mayEdit ? (
                  <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={13} />}
                          onClick={() => dropMilestone.mutate({ code: m.code })} title={t(lang, 'Снять веху', 'Remove milestone')} />
                ) : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {!rows.length ? (
        <EmptyState icon="file-plus" title={t(lang, 'В пакете нет документов', 'No documents in the package')}
                    text={t(lang, 'Прогресс узла считается по его документам и по вложенным пакетам.',
                                 'The node progress comes from its own documents and its children.')} />
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
          <thead>
            <tr style={{ background: 'var(--surface-sunken)', color: 'var(--text-subtle)', textAlign: 'left' }}>
              <th style={{ padding: '8px 16px', fontWeight: 500 }}>{t(lang, 'Документ', 'Document')}</th>
              <th style={{ padding: '8px 10px', fontWeight: 500 }}>{t(lang, 'Дисц.', 'Disc.')}</th>
              <th style={{ padding: '8px 10px', fontWeight: 500 }}>{t(lang, 'Рев.', 'Rev.')}</th>
              <th style={{ padding: '8px 10px', fontWeight: 500 }}>{t(lang, 'Стадия', 'Stage')}</th>
              <th style={{ padding: '8px 10px', fontWeight: 500 }}>{t(lang, 'План', 'Plan')}</th>
              <th style={{ padding: '8px 10px', fontWeight: 500 }}>{t(lang, 'Факт', 'Fact')}</th>
              {/* Вес позиции виден рядом с прогрессом: без него цифра пакета
                  необъяснима — непонятно, почему закрытый документ её не двинул. */}
              <th style={{ padding: '8px 10px', fontWeight: 500, textAlign: 'right' }}>{t(lang, 'Вес', 'Weight')}</th>
              <th style={{ padding: '8px 10px', fontWeight: 500, textAlign: 'right' }}>{t(lang, 'Прогресс', 'Progress')}</th>
              {mayEdit ? <th style={{ width: 34 }} /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.no} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={{ padding: '8px 16px' }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)' }}>{d.no}</span>
                  <span style={{ display: 'block', color: 'var(--text-muted)' }}>{d.title}</span>
                </td>
                <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{d.disc}</td>
                <td style={{ padding: '8px 10px', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>{d.rev ?? '—'}</td>
                <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{lifeName(d.life, lang)}</td>
                <td style={{ padding: '8px 10px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{d.plan ?? '—'}</td>
                <td style={{ padding: '8px 10px', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{d.fact ?? '—'}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: d.weight === 1 ? 'var(--text-subtle)' : 'var(--text-strong)' }}>{d.weight}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)' }}>
                  {d.progress == null ? '—' : d.progress + '%'}
                </td>
                {mayEdit ? (
                  <td style={{ padding: '4px 8px' }}>
                    <Button variant="ghost" size="sm" iconLeft={<Icon name="x" size={13} />}
                            title={t(lang, 'Убрать из пакета', 'Remove from package')}
                            onClick={() => unassign.mutate({ code: node.code, doc: d.no })} />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/** Создание и правка узла одной формой: поля те же, различается заголовок. */
function PackageDialog({ lang, parent = null, edit, packages, onClose, onDone }: {
  lang: Lang
  parent?: string | null
  edit?: WorkPackage
  packages: WorkPackage[]
  onClose: () => void
  onDone: (code: string) => void
}) {
  const [code, setCode] = useState(edit?.code ?? '')
  const [title, setTitle] = useState(edit?.title ?? '')
  const [weight, setWeight] = useState(String(edit?.weight ?? 1))
  const [parentCode, setParentCode] = useState(edit ? edit.parent ?? '' : parent ?? '')

  const invalidate = () => [qk.sub('/work-packages'), qk.all('audit')]
  const create = useCommand<{ code: string; title: string; parent: string | null; weight: number }>({
    path: () => '/work-packages',
    body: (a) => a,
    invalidate,
  })
  const update = useCommand<{ title: string; parent: string | null; weight: number }>({
    method: 'PUT',
    path: () => '/work-packages/' + encodeURIComponent(edit?.code ?? ''),
    body: (a) => a,
    invalidate,
  })

  const busy = create.isPending || update.isPending
  const ready = title.trim() !== '' && (edit != null || code.trim() !== '') && Number(weight) > 0
  return (
    <Dialog open onClose={onClose} title={edit ? t(lang, 'Изменить пакет', 'Edit package') : t(lang, 'Новый пакет работ', 'New work package')}>
      <div style={{ display: 'grid', gap: 12, width: 460, maxWidth: '100%' }}>
        {!edit ? (
          <Field label={t(lang, 'Код', 'Code')}>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="WP-01" />
          </Field>
        ) : null}
        <Field label={t(lang, 'Наименование', 'Title')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t(lang, 'Насосная станция', 'Pump station')} />
        </Field>
        <Field label={t(lang, 'Внутри пакета', 'Parent')}>
          <Select value={parentCode} onChange={(e) => setParentCode(e.target.value)}>
            <option value="">{t(lang, '— верхний уровень —', '— top level —')}</option>
            {packages.filter((p) => p.code !== edit?.code).map((p) => (
              <option key={p.code} value={p.code}>{p.code} · {p.title}</option>
            ))}
          </Select>
        </Field>
        <Field label={t(lang, 'Вес в родителе', 'Weight in the parent')}>
          <Input value={weight} onChange={(e) => setWeight(e.target.value)} inputMode="decimal"
                 hint={t(lang, 'Доля пакета среди соседей. 1 — наравне со всеми.', 'Share among siblings. 1 — equal to the others.')} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={!ready || busy} onClick={() => {
            const body = { title: title.trim(), parent: parentCode || null, weight: Number(weight) }
            if (edit) update.mutate(body, { onSuccess: () => { onDone(edit.code); onClose() } })
            else create.mutate({ code: code.trim(), ...body }, { onSuccess: () => { onDone(code.trim()); onClose() } })
          }}>
            {edit ? t(lang, 'Сохранить', 'Save') : t(lang, 'Создать', 'Create')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Разложить документы: номера списком.
 *
 * <p>Строка на документ — тот же вид, в котором номера приходят из плана в
 * xlsx: вставить столбец в поле дешевле, чем грузить файл, а разбор одинаков.
 * Документ из другого пакета переносится, и сервер отвечает, сколько чего.
 */
function AssignDialog({ lang, code, onClose }: { lang: Lang; code: string; onClose: () => void }) {
  const [text, setText] = useState('')
  const [weight, setWeight] = useState('1')
  const assign = useCommand<{ docs: string[]; weight: number }, { added: number; moved: number; unknown: string[] }>({
    path: () => '/work-packages/' + encodeURIComponent(code) + '/docs',
    body: (a) => a,
    invalidate: () => [qk.sub('/work-packages'), qk.sub('/work-packages/' + encodeURIComponent(code)), qk.all('audit')],
  })
  const docs = text.split(/[\n,;\t]+/).map((s) => s.trim()).filter(Boolean)

  return (
    <Dialog open onClose={onClose} title={t(lang, 'Разложить документы', 'Assign documents') + ' · ' + code}>
      <div style={{ display: 'grid', gap: 12, width: 480, maxWidth: '100%' }}>
        <Field label={t(lang, 'Номера документов', 'Document numbers')}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8}
                    style={{ width: '100%', resize: 'vertical', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-default)', background: 'var(--surface-card)', color: 'var(--text-body)', font: 'var(--font-mono)', fontSize: 12 }} />
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 4 }}>
            {t(lang, 'По одному в строке — столбец из плана вставляется как есть. Документ из другого пакета будет перенесён.',
                  'One per line — paste a column from the plan. A document from another package is moved.')}
          </div>
        </Field>
        {/* Вес партии, а не каждой строки: раскладывают тоже партиями —
            комплект приходит одним списком и весит одинаково. Тот же диалог
            переоценивает уже разложенное: документ, который уже здесь,
            принимает новый вес. */}
        <Field label={t(lang, 'Вес каждой позиции', 'Weight of each item')}>
          <Input type="number" step="0.1" min="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} />
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 4 }}>
            {t(lang, 'Мера объёма — листы или трудоёмкость, решает проект. Единица — все позиции равны.',
                  'A measure of volume — sheets or man-hours, the project decides. One means all items are equal.')}
          </div>
        </Field>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
            {t(lang, `Строк: ${docs.length}`, `${docs.length} lines`)}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
            <Button variant="primary" disabled={!docs.length || Number(weight) <= 0 || assign.isPending}
                    onClick={() => assign.mutate({ docs, weight: Number(weight) }, {
                      onSuccess: (r) => {
                        onClose()
                        toast({
                          tone: r.unknown.length ? 'danger' : 'success',
                          title: t(lang, `Добавлено ${r.added}, перенесено ${r.moved}`, `${r.added} added, ${r.moved} moved`),
                          text: r.unknown.length
                            ? t(lang, `Не найдено в реестре: ${r.unknown.slice(0, 5).join(', ')}${r.unknown.length > 5 ? '…' : ''}`,
                                     `Not in the register: ${r.unknown.slice(0, 5).join(', ')}${r.unknown.length > 5 ? '…' : ''}`)
                            : '',
                        })
                      },
                    })}>
              {t(lang, 'Разложить', 'Assign')}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

/** Веха: до какой стадии и к какой дате. Факт не спрашивается — он считается. */
function MilestoneDialog({ lang, code, onClose }: { lang: Lang; code: string; onClose: () => void }) {
  const [ms, setMs] = useState('')
  const [title, setTitle] = useState('')
  const [life, setLife] = useState('afd')
  const [plan, setPlan] = useState('')
  const create = useCommand<{ code: string; pkg: string; title: string; life: string; plan: string }>({
    path: () => '/milestones',
    body: (a) => a,
    invalidate: () => [qk.sub('/work-packages'), qk.all('audit')],
  })
  const ready = ms.trim() !== '' && title.trim() !== '' && plan !== ''

  return (
    <Dialog open onClose={onClose} title={t(lang, 'Веха пакета', 'Package milestone') + ' · ' + code}>
      <div style={{ display: 'grid', gap: 12, width: 460, maxWidth: '100%' }}>
        <Field label={t(lang, 'Код', 'Code')}>
          <Input value={ms} onChange={(e) => setMs(e.target.value)} placeholder="MS-01" />
        </Field>
        <Field label={t(lang, 'Наименование', 'Title')}>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t(lang, 'Выдача в производство работ', 'Issue for construction')} />
        </Field>
        <Field label={t(lang, 'Стадия', 'Stage')}>
          <Select value={life} onChange={(e) => setLife(e.target.value)}>
            {['received', 'review', 'crs', 'reissue', 'approved', 'afd'].map((l) => (
              <option key={l} value={l}>{lifeName(l, lang)}</option>
            ))}
          </Select>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 4 }}>
            {t(lang, 'Веха достигнута, когда ВСЕ документы пакета дошли до этой стадии',
                  'Reached when EVERY document of the package gets there')}
          </div>
        </Field>
        <Field label={t(lang, 'Плановая дата', 'Planned date')}>
          <Input type="date" value={plan} onChange={(e) => setPlan(e.target.value)} />
        </Field>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" disabled={!ready || create.isPending}
                  onClick={() => create.mutate({ code: ms.trim(), pkg: code, title: title.trim(), life, plan },
                    { onSuccess: () => onClose() })}>
            {t(lang, 'Поставить веху', 'Set milestone')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

/** Веса стадий: сколько процентов прогресса даёт каждое состояние документа. */
function WeightsDialog({ lang, weights, onClose }: { lang: Lang; weights: Record<string, number>; onClose: () => void }) {
  const set = useCommand<{ life: string; pct: number }>({
    method: 'PUT',
    path: (a) => '/progress-weights/' + encodeURIComponent(a.life),
    body: (a) => ({ pct: a.pct }),
    invalidate: () => [qk.sub('/work-packages'), qk.all('audit')],
  })
  const order = ['planned', 'received', 'review', 'crs', 'reissue', 'approved', 'afd']

  return (
    <Dialog open onClose={onClose} title={t(lang, 'Веса стадий', 'Stage weights')}>
      <div style={{ display: 'grid', gap: 10, width: 420, maxWidth: '100%' }}>
        {order.map((l) => (
          <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ flex: 1, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>{lifeName(l, lang)}</span>
            <Input style={{ width: 90 }} inputMode="numeric" defaultValue={String(weights[l] ?? 0)}
                   onBlur={(e) => {
                     const pct = Number(e.target.value)
                     if (Number.isFinite(pct) && pct !== (weights[l] ?? 0)) set.mutate({ life: l, pct })
                   }} />
            <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>%</span>
          </div>
        ))}
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Аннулированные документы в расчёт не входят вовсе.', 'Void documents are outside the calculation entirely.')}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>{t(lang, 'Готово', 'Done')}</Button>
        </div>
      </div>
    </Dialog>
  )
}
